import type { IPublicClientApplication } from "@azure/msal-browser";
import type { Edge, Node } from "@xyflow/react";

export enum DocumentViewMode {
    TABLE = "table",
    EXPLODE = "explode",
}

export enum NodeKind {
    BUCKET = "bucket",
}

export enum NodeRole {
    ROOT = "root",
    GROUP = "group",
    TITLE = "title",
}

export interface BucketSelection {
    field: string;
    value: string;
}

export interface SelectableField {
    value: string;
    label: string;
}

export interface CountBucket {
    value: string;
    count: number;
}

export interface FacetValue {
    key: string;
    doc_count: number;
}

export interface FacetField {
    field: string;
    facets: FacetValue[];
}

export interface FacetFilter {
    key: string;
    value: string[];
}

export interface FetchFacetsParams {
    instance: IPublicClientApplication;
    index: string;
    filters: FacetFilter[];
    maxResult?: number;
}

export interface Cluster {
    field: string;
    label: string;
    buckets: CountBucket[];
    distinctCount: number;
}

export interface OverlayTarget {
    field: string;
    label: string;
}

export interface OverlayState extends OverlayTarget {
    page: number;
}

export interface OverlayData extends OverlayState {
    buckets: CountBucket[];
}

export interface BuildFacetGraphParams {
    categories: CountBucket[];
    categoryField: string;
    categoryLabel: string;
    rootLabel: string;
    gridPage?: number;
    selected?: BucketSelection;
    clusters: Cluster[];
    subLoading?: boolean;
    overlay?: OverlayData;
}

export interface SubFacetsQueryConfig {
    instance: IPublicClientApplication;
    index: string;
    locks: BucketSelection[];
}

export interface UseOverviewGraphParams {
    index: string;
    rootLabel: string;
    category: string;
    categoryLabel: string;
    fields: SelectableField[];
    gridPage?: number;
    selected?: BucketSelection;
    overlay?: OverlayState;
}

export type ExplodeNodeData = {
    label: string;
    role: NodeRole;
    selection: BucketSelection[];
    count?: number;
    dimmed?: boolean;
    focused?: boolean;
    caption?: string;
    title?: boolean;
    skeleton?: boolean;
    muted?: boolean;
    overlay?: OverlayTarget;
    overlayPage?: number;
    gridPage?: number;
};

export interface ExplodeFlow {
    nodes: Node<ExplodeNodeData>[];
    edges: Edge[];
}

export interface ExpandGraphProps {
    index: string;
    rootLabel: string;
    category: string;
    categoryLabel: string;
    fields: SelectableField[];
    selected?: BucketSelection;
    overlay?: OverlayState;
    gridPage: number;
    onGridPage: (page: number) => void;
    onSelect: (selection: BucketSelection) => void;
    onClearSelection: () => void;
    onOpen: (selection: BucketSelection[]) => void;
    onOpenOverlay: (overlay: OverlayState) => void;
    onCloseOverlay: () => void;
}

export interface DocumentViewsProps {
    index: string;
    rootLabel: string;
    datasourceLabel: string;
    fields: SelectableField[];
    onFilteredCountChange?: (count: number | undefined) => void;
}
